import { Device, type DeviceInfo } from '@capacitor/device';
import { runningUnderNativePlatform } from './running-under-native-platform';

// For dev purposes
export const simulatePhone = localStorage.getItem('simulatePhone') === 'TRUE';

// Initialize device info lazily to avoid errors when module loads in browser
let deviceInfo: DeviceInfo | undefined = undefined;
let deviceInfoPromise: Promise<DeviceInfo | undefined> | undefined = undefined;

function getDeviceInfo(): Promise<DeviceInfo | undefined> {
  if (!deviceInfoPromise) {
    if (runningUnderNativePlatform()) {
      deviceInfoPromise = Device.getInfo()
        .then((info) => {
          deviceInfo = info;
          return info;
        })
        .catch((e) => {
          console.error('Failed to get device info:', e);
          return undefined;
        });
    } else {
      deviceInfoPromise = Promise.resolve(undefined);
    }
  }
  return deviceInfoPromise;
}

// Synchronous version for backward compatibility - may return undefined if not loaded yet
function getDeviceInfoSync(): DeviceInfo | undefined {
  // Trigger loading if not started
  if (!deviceInfoPromise) {
    getDeviceInfo();
  }
  return deviceInfo;
}

function isPhone(deviceInfo: DeviceInfo | undefined): boolean {
  if (deviceInfo === undefined) {
    // When running under native platform but device info not loaded yet, assume phone
    // This is safer than assuming desktop, as it preserves mobile UI until we know for sure
    return runningUnderNativePlatform();
  }
  if (deviceInfo.platform === 'ios') {
    return deviceInfo.model.startsWith('iPhone');
  } else if (deviceInfo.platform === 'android') {
    const widthDp = window.innerWidth / window.devicePixelRatio;
    const heightDp = window.innerHeight / window.devicePixelRatio;
    const minDp = Math.min(widthDp, heightDp);
    return minDp <= 600;
  }
  return false;
}

export function runningOnPhone(): boolean {
  if (simulatePhone) {
    return true;
  }
  if (!runningUnderNativePlatform()) {
    return false;
  }
  return isPhone(getDeviceInfoSync());
}

export async function runningOnPhoneAsync(): Promise<boolean> {
  if (simulatePhone) {
    return true;
  }
  if (!runningUnderNativePlatform()) {
    return false;
  }
  const info = await getDeviceInfo();
  return isPhone(info);
}
